import { auth, db } from "../../config/firebase";
import { createUserWithEmailAndPassword, signOut, signInWithEmailAndPassword, setPersistence, browserSessionPersistence, updateEmail, updatePassword, reauthenticateWithCredential, EmailAuthProvider, sendEmailVerification } from "firebase/auth";
import { collection, setDoc, query, where, getDocs, doc, getDoc, updateDoc } from "firebase/firestore";

export const getCurrentUser = () => {
    return auth.currentUser;
}

// Re-authenticate user (required for sensitive operations)
export const reauthenticate = async (password) => {
    try {
        const user = auth.currentUser;
        const credential = EmailAuthProvider.credential(user.email, password);
        await reauthenticateWithCredential(user, credential);
        return { success: true };
    } catch (error) {
        console.error("Reauthentication error:", error);
        return { 
            success: false, 
            error: "Incorrect password. Please verify your current password."
        };
    }
};

// Update email
export const updateUserEmail = async (newEmail, password) => {
    try {
        // First re-authenticate
        const reauth = await reauthenticate(password);
        if (!reauth.success) return reauth;
        
        const user = auth.currentUser;
        await sendEmailVerification(user, { newEmail });
        
        return { success: true,
            message: "Verification email sent. Please check your inbox to confirm the new email address."
         };
    } catch (error) {
        console.error("Email update error:", error);
        let errorMessage = "Failed to update email";
        if (error.code === 'auth/email-already-in-use') {
            errorMessage = "Email is already in use by another account";
        } else if (error.code === 'auth/invalid-email') {
            errorMessage = "Invalid email format";
        }
        else if (error.code === 'auth/requires-recent-login') {
            errorMessage = "Please sign out and sign in again before changing your email";
        } else if (error.code === 'auth/operation-not-allowed') {
            errorMessage = "Email verification is required. Please check your inbox.";
        }
        return { success: false, error: errorMessage };
    }
};

// Update username
export const updateUsername = async (newUsername) => {
    try {
        const user = auth.currentUser;
        
        // Update Firestore
        await updateDoc(doc(db, "users", user.uid), {
            username: newUsername
        });
        
        // Update session storage
        sessionStorage.setItem('username', newUsername);
        
        return { success: true };
    } catch (error) {
        console.error("Username update error:", error);
        return { success: false, error: "Failed to update username" };
    }
};

// Update password
export const updateUserPassword = async (currentPassword, newPassword) => {
    try {
        // First re-authenticate
        const reauth = await reauthenticate(currentPassword);
        if (!reauth.success) return reauth;
        
        const user = auth.currentUser;
        await updatePassword(user, newPassword);
        
        return { success: true };
    } catch (error) {
        console.error("Password update error:", error);
        let errorMessage = "Failed to update password";
        if (error.code === 'auth/weak-password') {
            errorMessage = "Password is too weak. Please use at least 6 characters.";
        }
        return { success: false, error: errorMessage };
    }
};


export const signUp = async (email, password, username) => {
    try {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const uid = userCredential.user.uid;

        // Use setDoc with an explicit document ID
        await setDoc(doc(db, "users", uid), {
            uid: uid,
            email: email,
            username: username,
            createdAt: new Date()
        });

        sessionStorage.setItem('uid', uid);
        sessionStorage.setItem('username', username);
        sessionStorage.setItem('email', email)
        return { success: true };
    } catch (error) {
        console.error("Sign up error:", error);
        let errorMessage = "Failed to create account";
        if (error.code === 'auth/email-already-in-use') {
            errorMessage = "Email is already in use";
        } else if (error.code === 'auth/invalid-email') {
            errorMessage = "Invalid email format";
        } else if (error.code === 'auth/weak-password') {
            errorMessage = "Password is too weak";
        }
        return { success: false, error: errorMessage };
    }
}

export const login = async (email, password) => {
    try {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);

        // // Set up the query
        // const q = query(collection(db, "users"), where("uid", "==", userCredential.user.uid));
        // // Execute the query
        // const querySnapshot = await getDocs(q);

        // // Extract the data from the document
        // let userData = null;
        // querySnapshot.forEach((doc) => {
        //     userData = doc.data();
        // });
        // if (userData) {
        //     sessionStorage.setItem('uid', userData.uid);
        //     sessionStorage.setItem('username', userData.username);
        //     sessionStorage.setItem('email', userData.email);
        //     return { success: true, userData };
        // }
        // return { success: false, error: "User data not found" };
        // Fetch the user document directly by UID
        const userDocRef = doc(db, "users", userCredential.user.uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
            const userData = userDoc.data();
            sessionStorage.setItem('uid', userData.uid);
            sessionStorage.setItem('username', userData.username);
            sessionStorage.setItem('email', userData.email);
            return { success: true, userData };
        }
        return { success: false, error: "User data not found" };
    } catch (error) {
        console.error("Login error:", error);
        let errorMessage = "Failed to log in";
        if (error.code === 'auth/user-not-found') {
            errorMessage = "No account found with this email";
        } else if (error.code === 'auth/wrong-password') {
            errorMessage = "Incorrect password";
        } else if (error.code === 'auth/invalid-email') {
            errorMessage = "Invalid email format";
        } else if (error.code === 'auth/too-many-requests') {
            errorMessage = "Too many failed login attempts. Please try again later";
        }
        return { success: false, error: error.message };
    }
}

export const logout = async () => {
    try {
        await signOut(auth);
        sessionStorage.removeItem('uid');
        sessionStorage.removeItem('username');
        sessionStorage.removeItem('email');
        sessionStorage.removeItem('experimentsCache');
        return { success: true };
    } catch (error) {
        console.error("Logout error:", error);
        return { success: false, error: "Failed to log out" };
    }
}

